import { useMemo } from 'react';
import type { Address } from 'viem';
import { useAccount, useReadContract, useReadContracts } from 'wagmi';
import { TARGET_CHAIN } from './config';
import { AGGREGATOR_V3_ABI, getChainlinkFeed } from './priceFeeds';
import { TIMEFRAME_MS, type Timeframe } from '../ai/signals';

// Number of price points to assemble. With stride sampling (below) these are
// spaced ~1 per resample bucket, so this is roughly the number of candles the
// backtest sees per timeframe.
const TARGET_POINTS = 240;
// How many previous aggregator phases to fall back into if the current phase
// can't supply enough strided samples.
const MAX_PREV_PHASES = 6;
// Rounds to look back when probing the feed's update cadence (seconds/round).
const PROBE_SPAN = 300n;
// Fallback cadence if the probe can't run (≈ measured testnet cadence).
const DEFAULT_SEC_PER_ROUND = 1200;

const MASK64 = (1n << 64n) - 1n;
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

export interface PricePoint {
  t: number;
  price: number;
}

// A Chainlink proxy roundId packs the phase in the high bits and the underlying
// aggregator's round in the low 64 bits: roundId = (phaseId << 64) | aggRound.
function encodeRoundId(phase: bigint, aggRound: bigint): bigint {
  return (phase << 64n) | aggRound;
}

/**
 * Builds a real historical price series from a Chainlink feed for a given
 * timeframe. Testnet feeds update only ~every 20 minutes, so covering months of
 * daily candles by reading consecutive rounds would need thousands of calls.
 * Instead we probe the feed's cadence and sample rounds at a stride (~1 round
 * per timeframe bucket), walking into previous aggregator phases when needed —
 * ~TARGET_POINTS reads regardless of timeframe. Reverting reads are filtered.
 */
export function useChainlinkHistory(symbol: string, timeframe: Timeframe = '4h'): PricePoint[] | undefined {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const feedAddress = getChainlinkFeed(symbol, readChainId);

  const { data: latest } = useReadContract({
    address: feedAddress,
    abi: AGGREGATOR_V3_ABI,
    functionName: 'latestRoundData',
    chainId: readChainId,
    query: { enabled: Boolean(feedAddress), refetchInterval: 60000 },
  });

  const { data: decimals } = useReadContract({
    address: feedAddress,
    abi: AGGREGATOR_V3_ABI,
    functionName: 'decimals',
    chainId: readChainId,
    query: { enabled: Boolean(feedAddress) },
  });

  const latestRoundId = latest?.[0];
  const latestUpdatedAt = latest?.[3];
  const currentPhase = latestRoundId !== undefined ? latestRoundId >> 64n : undefined;
  const currentAggRound = latestRoundId !== undefined ? latestRoundId & MASK64 : undefined;

  // ── Cadence probe: read a round PROBE_SPAN back to derive seconds/round ────
  const canProbe = currentPhase !== undefined && currentAggRound !== undefined && currentAggRound > PROBE_SPAN;
  const { data: probe } = useReadContract({
    address: feedAddress,
    abi: AGGREGATOR_V3_ABI,
    functionName: 'getRoundData',
    args: canProbe ? [encodeRoundId(currentPhase!, currentAggRound! - PROBE_SPAN)] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(feedAddress) && canProbe },
  });

  const secPerRound = useMemo(() => {
    if (probe && latestUpdatedAt !== undefined) {
      const older = probe[3];
      const delta = Number(latestUpdatedAt - older) / Number(PROBE_SPAN);
      if (delta > 0) return delta;
    }
    return DEFAULT_SEC_PER_ROUND;
  }, [probe, latestUpdatedAt]);

  const stride = useMemo(() => {
    const intervalSec = TIMEFRAME_MS[timeframe] / 1000;
    return BigInt(Math.max(1, Math.round(intervalSec / secPerRound)));
  }, [timeframe, secPerRound]);

  // ── Previous-phase aggregator addresses (fallback for shallow phases) ──────
  const prevPhases = useMemo(() => {
    if (currentPhase === undefined) return [];
    const phases: bigint[] = [];
    for (let k = 1n; k <= BigInt(MAX_PREV_PHASES); k++) {
      const p = currentPhase - k;
      if (p >= 1n) phases.push(p);
    }
    return phases;
  }, [currentPhase]);

  const phaseAggContracts = useMemo(
    () =>
      feedAddress
        ? prevPhases.map(
            (p) =>
              ({
                address: feedAddress,
                abi: AGGREGATOR_V3_ABI,
                functionName: 'phaseAggregators',
                args: [Number(p)],
                chainId: readChainId,
              }) as const,
          )
        : [],
    [feedAddress, prevPhases, readChainId],
  );

  const { data: phaseAggs } = useReadContracts({
    contracts: phaseAggContracts,
    query: { enabled: phaseAggContracts.length > 0 },
  });

  const prevAggregators = useMemo(() => {
    if (!phaseAggs) return [];
    const out: { phase: bigint; address: Address }[] = [];
    phaseAggs.forEach((r, i) => {
      if (r.status !== 'success') return;
      const addr = r.result as Address;
      if (addr && addr.toLowerCase() !== ZERO_ADDRESS) out.push({ phase: prevPhases[i], address: addr });
    });
    return out;
  }, [phaseAggs, prevPhases]);

  const aggLatestContracts = useMemo(
    () =>
      prevAggregators.map(
        (a) =>
          ({
            address: a.address,
            abi: AGGREGATOR_V3_ABI,
            functionName: 'latestRound',
            chainId: readChainId,
          }) as const,
      ),
    [prevAggregators, readChainId],
  );

  const { data: aggLatests } = useReadContracts({
    contracts: aggLatestContracts,
    query: { enabled: aggLatestContracts.length > 0 },
  });

  // ── Assemble strided proxy roundIds across phases ─────────────────────────
  const roundContracts = useMemo(() => {
    if (!feedAddress || currentPhase === undefined || currentAggRound === undefined) return [];
    const ids: bigint[] = [];

    const phasesOrdered: { phase: bigint; start: bigint }[] = [{ phase: currentPhase, start: currentAggRound }];
    prevAggregators.forEach((agg, i) => {
      const res = aggLatests?.[i];
      if (res && res.status === 'success') phasesOrdered.push({ phase: agg.phase, start: res.result as bigint });
    });

    for (const { phase, start } of phasesOrdered) {
      for (let r = start; r >= 1n && ids.length < TARGET_POINTS; r -= stride) {
        ids.push(encodeRoundId(phase, r));
      }
      if (ids.length >= TARGET_POINTS) break;
    }

    return ids.map(
      (roundId) =>
        ({
          address: feedAddress,
          abi: AGGREGATOR_V3_ABI,
          functionName: 'getRoundData',
          args: [roundId],
          chainId: readChainId,
        }) as const,
    );
  }, [feedAddress, currentPhase, currentAggRound, prevAggregators, aggLatests, stride, readChainId]);

  const { data: rounds } = useReadContracts({
    contracts: roundContracts,
    query: { enabled: roundContracts.length > 0 },
  });

  return useMemo(() => {
    if (!rounds || decimals === undefined) return undefined;
    const series: PricePoint[] = [];
    for (const round of rounds) {
      if (round.status !== 'success') continue;
      const [, answer, , updatedAt] = round.result as readonly [bigint, bigint, bigint, bigint, bigint];
      if (answer <= 0n || updatedAt === 0n) continue;
      series.push({ t: Number(updatedAt) * 1000, price: Number(answer) / 10 ** decimals });
    }
    series.sort((a, b) => a.t - b.t);
    return series;
  }, [rounds, decimals]);
}
